import { alsoShipped, projects } from '../../data/site';

const platformNames = (platforms) => platforms.map((p) => p.name).join(' · ');

/*--------------------
* Store buttons the platform name IS the link, so a client can click
* iOS / Android / Web straight through to the listing in a new tab.
* A platform with no href (delisted, or web behind a login) stays a
* plain chip rather than a dead link.
----------------------*/
function PlatformLinks({ platforms, size = 'btn' }) {
  if (!platforms || platforms.length === 0) return null;

  return (
    <div className="mt-6 flex flex-wrap items-center gap-2.5">
      {platforms.map(({ name, href }) =>
        href ? (
          <a
            key={name}
            className={size === 'chip' ? 'chip hover:!text-chalk' : 'btn btn-ghost btn-sm'}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open ${name} listing in a new tab`}
          >
            {name}
            <span aria-hidden="true"> &#8599;</span>
          </a>
        ) : (
          <span key={name} className="chip opacity-60">
            {name}
          </span>
        )
      )}
    </div>
  );
}

/*--------------------
* Artwork wide screenshot, app icon, or a designed placeholder
----------------------*/
function ProjectArt({ project }) {
  const [from, to] = project.accent;
  const wash = `linear-gradient(140deg, ${from}22, ${to}14 55%, rgb(var(--panel)))`;

  /* A real screenshot fills the frame. */
  if (project.image) {
    return (
      <div className="project-art project-art-shot bg-surface">
        <img
          src={project.image}
          title={project.title}
          alt={`${project.title} app screenshot`}
          loading="lazy"
          decoding="async"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-transparent dark:from-canvas/60" />
      </div>
    );
  }

  /* An app icon is square and may be light or dark, so it sits contained on
     the accent wash instead of being cropped full-bleed. */
  if (project.icon) {
    return (
      <div className="project-art project-art-fallback" style={{ background: wash }}>
        <div className="relative z-[1] px-6 text-center">
          <img
            src={project.icon}
            alt={`${project.title} app icon`}
            loading="lazy"
            decoding="async"
            width="512"
            height="512"
            className="project-art-icon mx-auto h-20 w-20 rounded-[22px] object-cover shadow-[0_18px_40px_-16px_rgb(0_0_0/.55)] ring-1 ring-tint/15 lg:h-24 lg:w-24"
          />
          <div className="mt-4 font-display text-[20px] font-[600] tracking-[-.01em] text-chalk">
            {project.title}
          </div>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-[.16em] text-tint/55">
            {platformNames(project.platforms)}
          </div>
        </div>
        <div
          className="aurora h-[220px] w-[220px] opacity-50"
          style={{ background: from, top: '-40px', right: '-40px' }}
        />
      </div>
    );
  }

  return (
    <div className="project-art project-art-fallback" style={{ background: wash }}>
      <div className="relative z-[1] px-6 text-center">
        <div
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-tint/15 text-[26px] text-tint/90 backdrop-blur-md"
          style={{ background: `linear-gradient(140deg, ${from}55, ${to}33)` }}
          aria-hidden="true"
        >
          {project.glyph}
        </div>
        <div className="mt-4 font-display text-[20px] font-[600] tracking-[-.01em] text-chalk">
          {project.title}
        </div>
        <div className="mt-1 font-mono text-[11px] uppercase tracking-[.16em] text-tint/55">
          {platformNames(project.platforms)}
        </div>
      </div>
      <div
        className="aurora h-[220px] w-[220px] opacity-50"
        style={{ background: from, top: '-40px', right: '-40px' }}
      />
    </div>
  );
}

/*--------------------
* Project row
----------------------*/
function Project({ project, index }) {
  const flip = index % 2 === 1;

  return (
    <article className="project-card reveal glass glass-hover card-sheen overflow-hidden p-5 lg:p-7">
      <div className="grid items-center gap-7 lg:grid-cols-12 lg:gap-10">
        <div className={`lg:col-span-5 ${flip ? 'lg:order-last' : ''}`}>
          <ProjectArt project={project} />
        </div>

        <div className="lg:col-span-7">
          <div className="flex flex-wrap items-center gap-3">
            <span className="chip chip-accent font-mono text-[11px] uppercase tracking-[.14em]">
              {project.tag}
            </span>
            <span className="font-mono text-[11px] tracking-[.14em] text-tint/50">
              {project.year}
            </span>
          </div>

          <h3 className="mt-4 font-display text-[26px] font-[600] leading-[1.15] tracking-[-.02em] text-chalk lg:text-[32px]">
            {project.title}
            <span className="block text-[15px] font-[500] text-muted lg:text-[17px]">
              {project.subtitle}
            </span>
          </h3>

          <p className="mt-4 text-[15px] leading-[1.72] text-muted">{project.body}</p>

          <ul className="mt-5 space-y-2">
            {project.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2.5 text-[14px] text-muted">
                <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-cyan-brand" />
                {h}
              </li>
            ))}
          </ul>

          <PlatformLinks platforms={project.platforms} />
        </div>
      </div>
    </article>
  );
}

/*--------------------
* Compact card for the rest of the shipped work
----------------------*/
function ShippedCard({ item, index }) {
  const [from, to] = item.accent;

  return (
    <div
      className="reveal glass glass-hover flex flex-col p-5"
      style={{ transitionDelay: `${index * 0.07}s` }}
    >
      <div className="flex items-center gap-3.5">
        {item.icon ? (
          <img
            src={item.icon}
            alt={`${item.title} app icon`}
            loading="lazy"
            decoding="async"
            width="512"
            height="512"
            className="h-11 w-11 shrink-0 rounded-[13px] object-cover ring-1 ring-tint/15"
          />
        ) : (
          <div
            className="h-11 w-11 shrink-0 rounded-[13px] ring-1 ring-tint/15"
            style={{ background: `linear-gradient(140deg, ${from}55, ${to}33)` }}
            aria-hidden="true"
          />
        )}
        <h4 className="font-display text-[17px] font-[600] leading-tight tracking-[-.01em] text-chalk">
          {item.title}
        </h4>
      </div>

      <p className="mt-3.5 flex-1 text-[14px] leading-[1.6] text-muted">{item.note}</p>

      <PlatformLinks platforms={item.platforms} size="chip" />
    </div>
  );
}

/*--------------------
* Work Section
----------------------*/
export default function Work() {
  return (
    <section id="work" className="section">
      <div className="container">
        <div className="reveal max-w-[720px]">
          <span className="kicker">Selected work</span>
          <h2 className="section-title mt-4">
            Apps in people&rsquo;s hands,
            <br className="hidden sm:block" /> not just in repos.
          </h2>
          <p className="lede mt-5">
            A slice of the 28+ production apps I&rsquo;ve shipped the ones where the
            interesting problem was AI, computer vision or getting a complex product
            through store review.
          </p>
        </div>

        <div className="mt-12 space-y-5 lg:mt-16 lg:space-y-6">
          {projects.map((project, i) => (
            <Project key={project.title} project={project} index={i} />
          ))}
        </div>

        {/* Also shipped */}
        <div className="mt-14 lg:mt-20">
          <h3 className="reveal font-mono text-[11px] uppercase tracking-[.2em] text-muted">
            Also shipped
          </h3>
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {alsoShipped.map((item, i) => (
              <ShippedCard key={item.title} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
